import React, { useEffect, useState } from 'react';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { handleWrongPlatformError } from '../../utils/wrongPlatform';
import { buildAuth0ShiftRedirectUrl, persistAuth0Grant } from '../../utils/auth0Session';

interface LoginModalProps {
  header: string;
  para: any;
  isOpen: boolean;
  object: any;
  setObject: any;
  onClose: () => void;
  onChange: () => void;
  onLogin: (status: boolean) => void;
  loader: boolean;
  onForgotPassword?: () => void;
}

const Login: React.FC<LoginModalProps> = ({
  header,
  para,
  isOpen,
  onClose,
  onChange,
  onLogin,
  setObject,
  object,
  loader,
  onForgotPassword,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    if (object?.password?.length >= 8) {
      setError(null);
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (object?.email && emailRegex.test(object.email)) {
      setError(null);
    }
  }, [object?.email, object?.password]);

  const handleFormSubmit = async () => {
    const redirect_uri = `${window.location.origin}/shift?defaultCurrency=${object.defaultCurrency}`;

    if (!object) {
      setError('Form data is not properly initialized.');
      return;
    }

    if (!object.email?.trim() || !object.password?.trim()) {
      setError('All fields are required.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(object.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (object.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setIsLoading(true);
    try {
      onLogin(true);
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URI}/auth0/login`, {
        email: object.email,
        password: object.password,
      });

      if (response.data.success && !response.data.mfa_required) {
        const grant = response.data.data;
        persistAuth0Grant(grant);
        window.location.href = buildAuth0ShiftRedirectUrl(redirect_uri, grant);
      } else if (response.data.mfa_required) {
        setMfaToken(response.data.mfa_token);
      }
    } catch (err: any) {
      if (handleWrongPlatformError(err, setError)) {
        return;
      }
      const errorData = err?.response?.data;
      const errMessage = errorData?.message || 'Authentication failed';
      setError(errMessage);
    } finally {
      setIsLoading(false);
      onLogin(false);
    }
  };

  const enrollMFA = async (mfaToken: string) => {
    if (!mfaToken) {
      setError('MFA token is missing. Please try logging in again.');
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/auth0/mfa/associate`,
        { mfa_token: mfaToken }
      );

      if (response.data.success) {
        return response.data.data;
      }
    } catch (err: any) {
      console.error('MFA enrollment failed:', err.response?.data || err.message);
    }
  };

  useEffect(() => {
    if (mfaToken && !qrCode) {
      enrollMFA(mfaToken).then(data => setQrCode(data?.barcode_uri));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaToken]);

  const verifyOtp = async () => {
    const redirect_uri = `${window.location.origin}/shift?defaultCurrency=${object.defaultCurrency}`;

    if (!otp) {
      setError('MFA code is required.');
      return;
    }

    if (!mfaToken) {
      setError('MFA token is missing. Please try logging in again.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URI}/auth0/mfa/verify`, {
        mfa_token: mfaToken,
        otp: otp,
        defaultCurrency: object.defaultCurrency,
      });

      if (response.data.success) {
        const grant = response.data.data;
        persistAuth0Grant(grant);
        window.location.href = buildAuth0ShiftRedirectUrl(redirect_uri, grant);
      }
    } catch (mfaError: any) {
      if (handleWrongPlatformError(mfaError, setError)) {
        return;
      }
      console.error('MFA verification failed:', mfaError.response?.data || mfaError.message);
      const errorMessage =
        mfaError?.response?.data?.message || 'Invalid MFA code. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      onLogin(false);
    }
  };

  const handleGoogleFormSubmit = () => {
    const domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const client_id = process.env.REACT_APP_AUTH0!;
    const redirect_uri = `${window.location.origin}/shift?defaultCurrency=${object.defaultCurrency}`;

    if (!domain || !client_id || !redirect_uri) {
      setError('Auth0 configuration is missing. Please check environment variables.');
      return;
    }

    const authUrl =
      `https://${domain}/authorize?` +
      `response_type=token&` +
      `client_id=${client_id}&` +
      `redirect_uri=${encodeURIComponent(redirect_uri)}&` +
      `connection=google-oauth2&` +
      `scope=openid profile email`;

    window.location.href = authUrl;
  };

  return (
    <div className="login-modal-inner">
      {mfaToken ? (
        <>
          {qrCode && (
            <>
              <div className="qrCodeDiv" style={{ textAlign: 'center', marginTop: '20px' }}>
                <h4>Scan this QR Code with your Authenticator App</h4>
                <div className="qrCode">
                  <div className="innerQrCode">
                    <QRCode
                      size={180}
                      style={{
                        height: 'auto',
                        maxWidth: '100%',
                        width: '100%',
                      }}
                      value={qrCode}
                      viewBox={`0 0 256 256`}
                    />
                  </div>
                </div>
              </div>
              <div className="thenSeperator">
                <div className="sepLine"></div>
                <p>Then</p>
                <div className="sepLine"></div>
              </div>
            </>
          )}

          <div className="main-form-login">
            <label>Type OTP</label>
            <input
              placeholder="Type One-time password"
              value={otp}
              onChange={ev => setOtp(ev.target.value)}
            />
            {error && (
              <p className="signup-error" style={{ color: 'red' }}>
                {error}
              </p>
            )}
          </div>
          <div className="form-login-inner">
            <div className="para-parent">{para}</div>
            <div className="para-parent para-parent-btn">
              <button
                style={{
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  position: 'relative',
                }}
                onClick={verifyOtp}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="spinner-container">
                    <div className="spinner"></div>
                  </div>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="login-socail">
            <button
              className="combine-button"
              style={{ cursor: 'pointer', margin: '0px auto' }}
              onClick={handleGoogleFormSubmit}
            >
              <span className="btn-svg">
                <img alt="google.svg" src="./static/google.svg" />
              </span>
              <div className="btn-text">Sign in with Google</div>
            </button>
          </div>
          <p className="login-or">Or</p>
          <div className="main-form-login">
            <label>Email</label>
            <input
              placeholder="Email address"
              value={object?.email}
              onChange={ev => setObject({ ...object, email: ev.target.value })}
            />
            <label>Password</label>
            <div className="password-input-container">
              <input
                placeholder="Password"
                value={object?.password}
                type={showPassword ? 'text' : 'password'}
                onChange={ev => setObject({ ...object, password: ev.target.value })}
              />
              <span className="password-toggle-icon" onClick={togglePasswordVisibility}>
                {showPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#007aff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#007aff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </span>
            </div>
            {error && (
              <p className="login-error" style={{ color: 'red' }}>
                {error}
              </p>
            )}
            {onForgotPassword && (
              <div
                className="forgot-password-link"
                style={{
                  display: 'flex',
                  marginBottom: '10px',
                  textAlign: 'right',
                }}
              >
                <button
                  type="button"
                  onClick={onForgotPassword}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#007aff',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px',
                  }}
                >
                  Forgot Password?
                </button>
              </div>
            )}
            <div className="form-login-inner">
              <div className="para-parent">{para}</div>
              <div className="para-parent para-parent-btn">
                <button
                  style={{
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    position: 'relative',
                  }}
                  onClick={handleFormSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="spinner-container">
                      <div className="spinner"></div>
                    </div>
                  ) : (
                    header
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Login;
